import { useCallback, useEffect, useRef, useState } from 'react';

import { useSoyuzData } from '@spotahome/soyuz/client';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';
import { Mediator } from '@spotahome/soyuz-mediator';

import { useCookie } from '../utils/withCookie';
import { CHANNELS } from '../utils/constants';
import { useMediaQuery } from '../utils/breakpoints/breakpoints';

import './ZohoChatbot.scss';

import Spinner from '../Spinner/Spinner';

const BOOTSTRAP_SCRIPT_ID = 'zoho-chat-bootstrap-script';
const WIDGET_SCRIPT_ID = 'zoho-chat-widget-script';
const TWO_WEEKS = 60 * 60 * 24 * 14; // 2 weeks in seconds

const ZohoChatbot = () => {
  const [isZohoChatLoading, setIsZohoChatLoading] = useState(false);
  const [isZohoInitialized, setIsZohoInitialized] = useState(false);
  const [showZohoChat, setShowZohoChat] = useState(true);
  const [isZohoChatOpen, setIsZohoChatOpen] = useState(false);
  const hasRegisteredCloseHandler = useRef(false);
  const shouldStartChatOnReady = useRef(false);
  const { isBiggerThanTablet } = useMediaQuery();
  const { getCookie, setCookie } = useCookie();
  const soyuzData = useSoyuzData();

  const pageName = Object.keys(soyuzData)[0] || 'unknown';

  const hasUsedSpottyCat = getCookie('SpottyCatUsed') !== undefined;

  const isZohoReady = useCallback(
    () => Boolean(window.$zoho?.salesiq?.chat?.start),
    []
  );

  const queueZohoReadyCallback = useCallback(callback => {
    window.$zoho = window.$zoho || {};
    window.$zoho.salesiq = window.$zoho.salesiq || {};
    window.$zoho.salesiq.spotReadyCallbacks =
      window.$zoho.salesiq.spotReadyCallbacks || [];
    window.$zoho.salesiq.ready = () => {
      const callbacks = window.$zoho.salesiq.spotReadyCallbacks || [];
      callbacks.splice(0).forEach(readyCallback => readyCallback());
    };
    window.$zoho.salesiq.spotReadyCallbacks.push(callback);
  }, []);

  const loadScripts = useCallback(() => {
    const bootstrapScript = document.querySelector(`#${BOOTSTRAP_SCRIPT_ID}`);

    if (!bootstrapScript) {
      const script = document.createElement('script');
      script.id = BOOTSTRAP_SCRIPT_ID;
      script.defer = true;
      script.type = 'text/javascript';
      script.innerHTML =
        'window.$zoho=window.$zoho || {};$zoho.salesiq=$zoho.salesiq||{ready:function(){}}';
      document.body.appendChild(script);
    } else {
      window.$zoho = window.$zoho || {};
      window.$zoho.salesiq = window.$zoho.salesiq || {};
    }

    const widgetScript = document.querySelector(`#${WIDGET_SCRIPT_ID}`);
    if (widgetScript) {
      return;
    }

    const scriptSrc = document.createElement('script');
    scriptSrc.id = WIDGET_SCRIPT_ID;
    scriptSrc.defer = true;
    scriptSrc.type = 'text/javascript';
    scriptSrc.src =
      '//salesiq.zohopublic.com/widget?wc=siq05454a5f6626ff134ffe8042ca1fbb7f2505ed7276493711f51ad5ac9d30f99d';
    scriptSrc.onerror = () => {
      setIsZohoChatLoading(false);
    };
    document.body.appendChild(scriptSrc);
  }, []);

  const addAlreadyUsedClass = () => {
    const zohoChatElement = document.querySelector('#zsiq_float');
    if (zohoChatElement) {
      zohoChatElement.classList.add('zsiq-custom-used');
    }
  };

  useEffect(() => {
    if (isZohoReady()) {
      setIsZohoInitialized(true);
    }
  }, [isZohoReady]);

  useEffect(() => {
    const withChatInMobile = fn => () => {
      if (isBiggerThanTablet() && window.$zoho?.salesiq?.floatbutton) {
        window.$zoho?.salesiq.floatbutton.visible('show');
        setShowZohoChat(true);
        return;
      }

      fn();
    };

    const hideButton = withChatInMobile(() => {
      if (window.$zoho?.salesiq?.floatbutton) {
        window.$zoho.salesiq.floatbutton.visible('hide');
      }
      setShowZohoChat(false);
    });

    const showButton = withChatInMobile(() => {
      if (window.$zoho?.salesiq?.floatbutton) {
        window.$zoho.salesiq.floatbutton.visible('show');
      }
      setShowZohoChat(true);
    });

    Mediator.subscribe(CHANNELS.HOMECARD_MAP_OPEN, hideButton);
    Mediator.subscribe(CHANNELS.HOMECARD_MAP_CLOSE, showButton);
    Mediator.subscribe(CHANNELS.OPEN_EXPOSED_FILTER, hideButton);
    Mediator.subscribe(CHANNELS.CLOSE_EXPOSED_FILTER, showButton);
    Mediator.subscribe(CHANNELS.GOOGLE_ONE_TAP_SHOWN, hideButton);
    Mediator.subscribe(CHANNELS.GOOGLE_ONE_TAP_HIDDEN, showButton);

    return () => {
      Mediator.unsubscribe(CHANNELS.HOMECARD_MAP_OPEN, hideButton);
      Mediator.unsubscribe(CHANNELS.HOMECARD_MAP_CLOSE, showButton);
      Mediator.unsubscribe(CHANNELS.OPEN_EXPOSED_FILTER, hideButton);
      Mediator.unsubscribe(CHANNELS.CLOSE_EXPOSED_FILTER, showButton);
      Mediator.unsubscribe(CHANNELS.GOOGLE_ONE_TAP_SHOWN, hideButton);
      Mediator.unsubscribe(CHANNELS.GOOGLE_ONE_TAP_HIDDEN, showButton);
    };
  }, [showZohoChat, isBiggerThanTablet]);

  const initializeAndOpenChat = useCallback(() => {
    window.$zoho.salesiq.chat.online(() => {
      if (hasUsedSpottyCat) {
        addAlreadyUsedClass();
      }
    });

    if (!hasRegisteredCloseHandler.current) {
      window.$zoho.salesiq.floatwindow.close(() => {
        SoyuzAnalytics.sendGA4Event('send', {
          context: 'ZohoChat',
          action: 'Close',
          value: 'click',
          content_type: pageName
        });
      });
      hasRegisteredCloseHandler.current = true;
    }

    if (!showZohoChat) {
      window.$zoho.salesiq.floatbutton.visible('hide');
    } else if (shouldStartChatOnReady.current) {
      try {
        window.$zoho.salesiq.chat.start();
      } catch (error) {
        // ignore error
      }
      shouldStartChatOnReady.current = false;
    }

    setIsZohoInitialized(true);
    setIsZohoChatLoading(false);
  }, [hasUsedSpottyCat, pageName, showZohoChat]);

  useEffect(() => {
    if (!isZohoChatOpen) {
      return;
    }

    shouldStartChatOnReady.current = true;
    setCookie('SpottyCatUsed', 1, { maxAge: TWO_WEEKS });
    SoyuzAnalytics.sendGA4Event('send', {
      context: 'ZohoChat',
      action: 'Open',
      value: 'click',
      content_type: pageName
    });

    if (isZohoReady()) {
      initializeAndOpenChat();
      return;
    }

    queueZohoReadyCallback(() => {
      initializeAndOpenChat();
    });
    loadScripts();
  }, [
    initializeAndOpenChat,
    isZohoChatOpen,
    isZohoReady,
    loadScripts,
    pageName,
    queueZohoReadyCallback,
    setCookie
  ]);

  const handleClick = useCallback(() => {
    setIsZohoChatOpen(true);
    setIsZohoChatLoading(true);
  }, []);

  if (!showZohoChat) {
    return null;
  }

  return !isZohoInitialized || isZohoChatLoading ? (
    <div className="zoho-chat-iframe-wrap">
      <div
        id="zsiq_float_mock"
        onClick={isZohoChatLoading ? () => {} : handleClick}
        role="button"
        tabIndex={0}
      >
        {isZohoChatLoading ? (
          <div className="zoho-chat-iframe-loading">
            <Spinner />
          </div>
        ) : null}
        <img alt="Spotty Cat" />
      </div>
    </div>
  ) : null;
};

export default ZohoChatbot;
